import React, { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
// eslint-disable-next-line import/no-unresolved
import WaIcon from "images/integrations/wa.png";
import ButtonComponent from "../../components/ButtonMC";
import CallCustomerData from './CallCustomerData';
import { useButtonsDisabled } from '../../hooks/shared/ui/useButtonsDisabled';
import useWhatsappCallActions from '../../hooks/calls/useWhatsappCallActions';
import useWhatsappOnAnswer from '../../hooks/calls/useWhatsappOnAnswer';
import useRingtone from '../../hooks/shared/ui/useRingtone';
import { useTraceUpdate } from '../../hooks/shared/useDebugRenders';
import useVoiceOutboundFrom from '../../hooks/voice/application/useVoiceOutboundFrom';
import {
  findSipLineByPhone,
  formatOutboundLineLabel
} from '../../hooks/voice/domain/voiceSipStatus';
import { peekSipStatusCache } from '../../hooks/voice/infrastructure/voiceSipStatusCache';
import { normalizeVoiceE164 } from '../../hooks/voice/application/useVoiceTenantLines';

/**
 * IncomingCall Component
 *
 * Displays an incoming call information and allows answering or declining the call.
 *
 * @param {Object} props - Component props
 * @param {Object} props.callInfoData - Incoming call payload
 * @param {function} props.startActiveCall - Starts the active call UI
 */
const IncomingCallCard = memo(({
  callInfoData,
  startActiveCall
}) => {
  const { t } = useTranslation('Fonema');

  const customer = callInfoData?.customer ?? null;
  const callingLinePhone = callInfoData?.registerNumber || callInfoData?.voice?.to || '';
  const showCallingLine = Boolean(callInfoData?.voice?.to);
  const { enabledLines } = useVoiceOutboundFrom({ enabled: showCallingLine });

  // Hook dedicado para el evento onAnswer
  useWhatsappOnAnswer({ callInfoData, startActiveCall });

  // UI Layer
  const { buttonsDisabled, disableButtons } = useButtonsDisabled();

  // Actions Layer
  const { answerCall, declineCall } = useWhatsappCallActions({
    disableButtons,
    callInfoData
  });

  // Play ringtone while incoming card is visible; stop when active call starts.
  useRingtone('/sounds/incoming_call.mp3', { enabled: true });

  // Memoizar la clase del botón decline para optimización de renderizado
  const declineBtnClass = useMemo(() => (
    `tw-w-1/2 tw-bg-white !tw-text-red-500 !tw-border !tw-border-red-500${!buttonsDisabled ? ' hover:!tw-bg-gray-200' : ''}`
  ), [buttonsDisabled]);

  const callingLineLabel = useMemo(() => {
    const phone = normalizeVoiceE164(callingLinePhone);
    if (!phone) return '';

    const outboundLine = enabledLines.find((line) => line.businessE164 === phone);
    if (outboundLine) return formatOutboundLineLabel(outboundLine);

    const sipLine = findSipLineByPhone(peekSipStatusCache(), phone);
    return formatOutboundLineLabel({
      name: sipLine?.name,
      businessE164: sipLine?.phoneNumber || phone
    });
  }, [callingLinePhone, enabledLines]);

  // Debug re-renders con información de hooks
  useTraceUpdate('IncomingCall', {
    callInfoData,
    hasCustomer: !!customer,
    customerId: customer?.id,
    buttonsDisabled
  });

  return (
    <div
      className="tw-wrapper"
      style={{ outline: 'none', width: '100%' }}
      tabIndex={-1}
      aria-label={t('incoming_call')}
    >
      <div className="tw-bg-blue-950 tw-select-none tw-p-4 tw-rounded-xl tw-border-transparent">
        <div className="tw-flex tw-flex-row tw-align-middle">
          <img src={WaIcon} alt="WhatsApp" className="tw-w-6 tw-h-6" />
          <span className="tw-ml-1 tw-text-blue-100 tw-text-sm">{t('incoming_call')}</span>
        </div>
        <CallCustomerData customer={customer} phone={callInfoData.callerNumber} />
        {showCallingLine && callingLineLabel && (
          <div className="tw-text-xs tw-text-blue-200 tw-mt-1">
            {`${t('callingLine')}: ${callingLineLabel}`}
          </div>
        )}
        <div className="tw-flex tw-gap-1 tw-mt-2">
          {!buttonsDisabled && (
            <ButtonComponent
              text={t('decline')}
              className={declineBtnClass}
              icon="phone-outline"
              variant="DANGER"
              onClick={async () => {
                await declineCall();
              }}
            />
          )}
          <ButtonComponent
            text={t('answer')}
            className={buttonsDisabled ? 'tw-w-full' : 'tw-w-1/2'}
            icon="phone-outline"
            variant="SUCCESS"
            onClick={async () => {
              await answerCall();
            }}
            disabled={buttonsDisabled}
          />
        </div>
      </div>
    </div>
  );
});

IncomingCallCard.displayName = 'IncomingCallCard';

export default IncomingCallCard;
